const CLAVE_SESION = "PiSeunSesion";

const storageSesion = (storage) => storage ?? globalThis.sessionStorage;
const storageCompatibilidad = (storage) => storage ?? globalThis.localStorage;

const listaCodigos = (valor) => Array.from(new Set(
  (Array.isArray(valor) ? valor : [])
    .map((codigo) => String(codigo || "").trim())
    .filter(Boolean),
));

const numeroPositivo = (valor) => {
  const numero = Number(valor);
  return Number.isSafeInteger(numero) && numero > 0 ? numero : 0;
};

export const normalizarSesionLogin = (respuesta = {}) => ({
  IdentidadId: numeroPositivo(respuesta.IdentidadId ?? respuesta.Id),
  OperadorId: numeroPositivo(respuesta.OperadorId ?? respuesta.OperadorLocalId ?? respuesta.Id),
  EmpresaId: numeroPositivo(respuesta.EmpresaId ?? respuesta.Empresa_Id),
  sDescripcion: String(respuesta.sDescripcion ?? "").trim(),
  Mail: String(respuesta.Mail ?? respuesta.sMail ?? ""),
  Nombre: String(respuesta.Nombre ?? respuesta.sNombre ?? ""),
  Apellido: String(respuesta.Apellido ?? respuesta.sApellido ?? ""),
  AccessToken: String(respuesta.AccessToken ?? ""),
  RefreshToken: String(respuesta.RefreshToken ?? ""),
  ExpiresAtUtc: respuesta.ExpiresAtUtc ?? respuesta.TokenVenceUtc ?? null,
  Roles: listaCodigos(respuesta.Roles),
  Permisos: listaCodigos(respuesta.Permisos),
});

const guardarCompatibilidad = (sesion, storage) => {
  const destino = storageCompatibilidad(storage);
  if (!destino) return;
  destino.setItem("EmpresaId", String(sesion.EmpresaId));
  destino.setItem("Descripcion", sesion.sDescripcion);
  destino.setItem("Id", String(sesion.IdentidadId));
  destino.setItem("OperadorId", String(sesion.OperadorId));
  destino.setItem("Mail", sesion.Mail);
  destino.setItem("Nombre", sesion.Nombre);
  destino.setItem("Apellido", sesion.Apellido);
  destino.setItem("Roles", JSON.stringify(sesion.Roles));
  destino.setItem("Permisos", JSON.stringify(sesion.Permisos));
  destino.setItem("auth", "1");
  destino.setItem("Operador", JSON.stringify({
    Id: sesion.IdentidadId,
    OperadorLocalId: sesion.OperadorId,
    sMail: sesion.Mail,
    sNombre: sesion.Nombre,
    sApellido: sesion.Apellido,
    Empresa_Id: sesion.EmpresaId,
    sDescripcion: sesion.sDescripcion,
  }));
  // Una sesión nueva nunca conserva la contraseña legacy.
  destino.removeItem("Password");
};

export const guardarSesion = (respuesta, { sessionStorage, localStorage } = {}) => {
  const sesion = normalizarSesionLogin(respuesta);
  if (!sesion.EmpresaId || !sesion.IdentidadId) {
    throw new Error("La respuesta de inicio de sesión no contiene una identidad y empresa válidas.");
  }
  storageSesion(sessionStorage)?.setItem(CLAVE_SESION, JSON.stringify(sesion));
  guardarCompatibilidad(sesion, localStorage);
  return sesion;
};

export const obtenerSesion = (storage) => {
  try {
    const raw = storageSesion(storage)?.getItem(CLAVE_SESION);
    return raw ? normalizarSesionLogin(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
};

export const actualizarTokensSesion = (respuesta, opciones = {}) => {
  const actual = obtenerSesion(opciones.sessionStorage);
  if (!actual) throw new Error("No existe una sesión para renovar.");
  const renovada = normalizarSesionLogin({ ...actual, ...respuesta });
  if (!renovada.AccessToken || !renovada.RefreshToken) {
    throw new Error("La renovación no devolvió ambos tokens.");
  }
  return guardarSesion(renovada, opciones);
};

const CLAVES_COMPATIBILIDAD = [
  "DB", "Conexion", "Mail", "Password", "Descripcion", "Nombre", "Apellido",
  "Cuit", "Id", "OperadorId", "EmpresaId", "auth", "Operador", "Roles",
  "Permisos", "PcId", "ConfiguracionPcId", "CajaSesionActivaId",
];

export const limpiarSesion = ({ sessionStorage, localStorage } = {}) => {
  storageSesion(sessionStorage)?.removeItem(CLAVE_SESION);
  const destino = storageCompatibilidad(localStorage);
  CLAVES_COMPATIBILIDAD.forEach((clave) => destino?.removeItem(clave));
};

export const tienePermiso = (codigo, sesion = obtenerSesion()) =>
  Boolean(codigo && (
    sesion?.Permisos?.includes("*") || sesion?.Permisos?.includes(codigo)
  ));

export const tieneAlgunPermiso = (...codigos) => {
  const lista = codigos.length === 1 && Array.isArray(codigos[0]) ? codigos[0] : codigos;
  const sesion = obtenerSesion();
  return lista.some((codigo) => tienePermiso(codigo, sesion));
};

export const tieneRol = (codigo, sesion = obtenerSesion()) =>
  Boolean(codigo && sesion?.Roles?.includes(codigo));

// Con seguridad desactivada la API devuelve tokens vacíos: se conserva el acceso legacy.
export const puedeAcceder = (codigo, sesion = obtenerSesion()) =>
  Boolean(sesion && (!sesion.AccessToken || tienePermiso(codigo, sesion)));

export const PERMISOS = Object.freeze({
  SEGURIDAD_ADMINISTRAR: "Seguridad.Administrar",
  USUARIOS_ADMINISTRAR: "Usuarios.Administrar",
  VENTAS_VER: "Ventas.Ver",
  VENTAS_CREAR: "Ventas.Crear",
  VENTAS_ANULAR: "Ventas.Anular",
  PRECIOS_VER: "Precios.Ver",
  PRECIOS_MODIFICAR: "Precios.Modificar",
  REPORTES_VER: "Reportes.Ver",
  REPORTES_EXPORTAR: "Reportes.Exportar",
  REPORTES_VENTAS_VER: "Reportes.Ventas.Ver",
  REPORTES_INGRESOS_VER: "Reportes.Ingresos.Ver",
  REPORTES_PRODUCCION_VER: "Reportes.Produccion.Ver",
  REPORTES_DESPACHOS_VER: "Reportes.Despachos.Ver",
  REPORTES_STOCK_VER: "Reportes.Stock.Ver",
  REPORTES_CAJA_VER: "Reportes.Caja.Ver",
  COBROS_ADMINISTRAR: "Cobros.Administrar",
  CATALOGOS_VER: "Catalogos.Ver",
  CLIENTES_ADMINISTRAR: "Clientes.Administrar",
  CAJAS_ADMINISTRAR: "Cajas.Administrar",
  FACTURACION_ADMINISTRAR: "Facturacion.Administrar",
  FACTURACION_CONFIGURAR_IVA_PENDIENTE: "Facturacion.ConfigurarIvaPendiente",
  PRODUCCION_ADMINISTRAR: "Produccion.Administrar",
  CONFIGURACION_ADMINISTRAR: "Configuracion.Administrar",
  MERCADERIAS_ADMINISTRAR: "Mercaderias.Administrar",
  COMPROBANTES_VER: "Comprobantes.Ver",
  NOTAS_CREDITO_VER: "NotasCredito.Ver",
  NOTAS_CREDITO_EMITIR: "NotasCredito.Emitir",
  CUENTA_CORRIENTE_VER: "CuentaCorriente.Ver",
  CUENTA_CORRIENTE_IMPUTAR: "CuentaCorriente.Imputar",
  CUENTA_CORRIENTE_REVERTIR: "CuentaCorriente.Revertir",
  COBRANZAS_VER: "Cobranzas.Ver",
  COBRANZAS_REGISTRAR: "Cobranzas.Registrar",
  COBRANZAS_ANULAR: "Cobranzas.Anular",
  TRAZABILIDAD_VER: "Trazabilidad.Ver",
  PEDIDOS_ADMINISTRAR: "Pedidos.Administrar",
});

export const ROLES = Object.freeze({
  SOPORTE: "SOPORTE",
});

export const puedeCrearClientes = (sesion = obtenerSesion()) =>
  puedeAcceder(PERMISOS.VENTAS_CREAR, sesion) ||
  puedeAcceder(PERMISOS.CLIENTES_ADMINISTRAR, sesion);

export const puedeAdministrarClientes = (sesion = obtenerSesion()) =>
  puedeAcceder(PERMISOS.CLIENTES_ADMINISTRAR, sesion);

export { CLAVE_SESION };
